import axios, { type AxiosInstance } from 'axios';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  LoginResponse,
  RegisterResponse,
  UserCreatePayload,
  UserUpdatePayload,
  Organization,
  OrganizationCreatePayload,
  OrganizationUpdatePayload,
  TestSpec,
  AnalyzeRequest,
} from '../types';

// Vite inyecta variables SOLO en build-time.
function normalizeBaseUrl(raw?: string): string {
  let val = (raw ?? '').trim();

  // Auto-upgrade to HTTPS for production domain or if page is secure
  const isProd = val.includes('flowspecqa.com');
  const isSecurePage = typeof window !== 'undefined' && window.location.protocol === 'https:';

  // [Fix] Force internal routing through Nginx for production to bypass external proxy limits (upload size)
  if (typeof window !== 'undefined' && window.location.hostname.includes('flowspecqa.com')) {
    return '/api';
  }

  if ((isProd || isSecurePage) && val.startsWith('http://')) {
    val = val.replace('http://', 'https://');
  }

  if (!val) return '/api';

  const base = val.replace(/\/+$/, '');
  const isHttp = /^https?:\/\//i.test(base);

  if (isHttp && !/\/api$/i.test(base)) return `${base}/api`;
  return base;
}

const API_BASE_URL = normalizeBaseUrl(import.meta.env.VITE_API_URL);

export type SelectOption = { value: string; label: string };
export type TestConnectionResponse = { message: string };

class ApiClientService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
      timeout: 300000, // 5 minutes
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      // X-Org-ID from user data
      const userJson = localStorage.getItem('user');
      if (userJson) {
        try {
          const user = JSON.parse(userJson);
          if (user.org_id) {
            config.headers = config.headers ?? {};
            config.headers['X-Org-ID'] = user.org_id;
          }
        } catch (e) {
          console.error("Error parsing user for X-Org-ID", e);
        }
      }

      return config;
    });

    // 401 → limpiar sesión
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Avoid infinite loop if on login page
          if (window.location.pathname !== '/login') {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // ==========================================
  // Auth
  // ==========================================
  isAuthenticated() {
    return !!localStorage.getItem('token');
  }

  async getCurrentUser(skipCache = false): Promise<User | null> {
    if (!skipCache) {
      const cached = localStorage.getItem('user');
      if (cached) {
        try {
          return JSON.parse(cached) as User;
        } catch {
          localStorage.removeItem('user');
        }
      }
    }

    try {
      const { data } = await this.client.get<User>('/auth/me');
      localStorage.setItem('user', JSON.stringify(data));
      return data;
    } catch {
      // If verification fails, clear local storage to prevent loops
      if (skipCache) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      return null;
    }
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    const res = await this.client.post<LoginResponse>('/auth/login', data);
    const token = res.data.token ?? res.data.access_token;
    if (token) localStorage.setItem('token', token);
    if (res.data.user) localStorage.setItem('user', JSON.stringify(res.data.user));
    return res.data;
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const res = await this.client.post<RegisterResponse>('/auth/register', data);
    const token = res.data.token ?? res.data.access_token;
    if (token) localStorage.setItem('token', token);
    if (res.data.user) localStorage.setItem('user', JSON.stringify(res.data.user));
    return res.data;
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }

  // ==========================================
  // Auth0 OAuth & Email Verification
  // ==========================================
  async getGoogleAuthUrl(): Promise<{ authorization_url: string; state: string }> {
    const res = await this.client.get('/auth/google');
    return res.data;
  }

  async handleOAuthCallback(code: string, state: string): Promise<{ access_token: string; user: User }> {
    const res = await this.client.post('/auth/callback', { code, state });
    const token = res.data.access_token;
    if (token) localStorage.setItem('token', token);
    if (res.data.user) localStorage.setItem('user', JSON.stringify(res.data.user));
    return res.data;
  }

  async sendVerificationCode(email: string): Promise<{ message: string }> {
    const res = await this.client.post('/auth/send-verification', { email });
    return res.data;
  }

  async verifyEmail(email: string, code: string): Promise<{ message: string }> {
    const res = await this.client.post('/auth/verify-email', { email, code });
    return res.data;
  }

  // ==========================================
  // Configuración / Integraciones
  // ==========================================
  async getDomains(): Promise<SelectOption[]> {
    return (await this.client.get<SelectOption[]>('/config/domains')).data;
  }

  async getAiModels(provider?: string): Promise<SelectOption[]> {
    return (await this.client.get<SelectOption[]>('/config/ai-models', { params: { provider } })).data;
  }

  async getProjects(): Promise<any[]> {
    return (await this.client.get<any[]>('/config/projects')).data;
  }

  async getProjectConfig(id: string): Promise<any> {
    return (await this.client.get(`/config/projects/${id}`)).data;
  }

  async updateProject(id: string, payload: any): Promise<any> {
    return (await this.client.put(`/config/projects/${id}`, payload)).data;
  }

  async createProject(payload: Record<string, any>): Promise<any> {
    return (await this.client.post('/config/projects', payload)).data;
  }

  async deleteProject(id: string): Promise<any> {
    return (await this.client.delete(`/config/projects/${id}`)).data;
  }

  async migrateDb(): Promise<any> {
    return (await this.client.post('/config/migrate-db')).data;
  }

  // Templates
  async getTemplates(): Promise<any[]> {
    try {
      const res = await this.client.get('/templates');
      if (res.data && res.data.length > 0) return res.data;
      throw new Error("Empty templates");
    } catch (e) {
      console.warn("API templates failed, using fallback", e);
      return [
        { id: 'banking_core', name: 'Core Banking', description: 'Transacciones, cuentas y seguridad financiera.', industry: 'banking' },
        { id: 'retail_ecom', name: 'E-Commerce Retail', description: 'Catálogo, carrito, checkout y pagos.', industry: 'retail' },
        { id: 'telco_billing', name: 'Telco Billing', description: 'Facturación masiva y gestión de planes.', industry: 'telco' },
        { id: 'insurance_claims', name: 'Insurance Claims', description: 'Gestión de siniestros y pólizas.', industry: 'insurance' },
      ];
    }
  }

  // Oracles
  async getOracles(projectId: string): Promise<any[]> {
    const res = await this.client.get(`/oracles/projects/${projectId}/oracles`);
    return res.data;
  }

  // Entities
  async getEntities(projectId: string): Promise<any[]> {
    // Temporary fallback or implementation
    console.log('Fetching entities for', projectId);
    return [];
  }

  async createOracle(projectId: string, oracle: any): Promise<any> {
    const res = await this.client.post(`/oracles/projects/${projectId}/oracles`, oracle);
    return res.data;
  }

  async testJiraConnection(jiraUrl: string): Promise<TestConnectionResponse> {
    return (await this.client.post<TestConnectionResponse>('/config/test-connection/jira', { url: jiraUrl })).data;
  }

  async testConfluenceConnection(pageIdOrUrl: string): Promise<TestConnectionResponse> {
    return (await this.client.post<TestConnectionResponse>('/config/test-connection/confluence', { url: pageIdOrUrl })).data;
  }

  // ==========================================
  // Dashboard
  // ==========================================
  async getDashboardStats(days?: number): Promise<any> {
    return (await this.client.get('/dashboard/stats', { params: days ? { days } : undefined })).data;
  }

  async getQADashboardStats(): Promise<any> {
    return (await this.client.get('/dashboard/qa-stats')).data;
  }

  // ==========================================
  // Organizations
  // ==========================================
  async getOrganizations(): Promise<Organization[]> {
    return (await this.client.get<Organization[]>('/organizations')).data;
  }

  async createOrganization(data: OrganizationCreatePayload): Promise<Organization> {
    return (await this.client.post<Organization>('/organizations', data)).data;
  }

  async updateOrganization(id: string, data: OrganizationUpdatePayload): Promise<Organization> {
    return (await this.client.patch<Organization>(`/organizations/${id}`, data)).data;
  }

  async deleteOrganization(id: string): Promise<any> {
    return (await this.client.delete(`/organizations/${id}`)).data;
  }

  // ==========================================
  // Generation
  // ==========================================
  async checkContext(huId: string, projectId: string): Promise<any> {
    return (await this.client.get(`/generation/context/${huId}`, { params: { project_id: projectId } })).data;
  }

  // Copilot Analysis
  async analyzeUserStory(payload: AnalyzeRequest): Promise<TestSpec> {
    return (await this.client.post<TestSpec>('/generation/analyze', payload)).data;
  }

  async getSpecReview(projectId: string, entityId: string): Promise<any> {
    return (await this.client.post(`/spec-review/projects/${projectId}/entities/${entityId}/spec-review`)).data;
  }

  async generateTests(payload: Record<string, any>): Promise<any> {
    return (await this.client.post('/generation/generate', payload)).data;
  }

  async generateFromGherkin(file: File, framework: string, projectId: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('framework', framework);
    formData.append('project_id', projectId);

    return (await this.client.post('/automation/generate-from-gherkin', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // 2 minutes just in case
    })).data;
  }

  async getJob(jobId: string): Promise<any> {
    return (await this.client.get(`/generation/job/${jobId}`)).data;
  }

  async downloadExcel(runId: string): Promise<Blob> {
    const res = await this.client.get(`/generation/download/${runId}/excel`, { responseType: 'blob' });
    return res.data as Blob;
  }

  async downloadJson(runId: string): Promise<Blob> {
    const res = await this.client.get(`/generation/download/${runId}/json`, { responseType: 'blob' });
    return res.data as Blob;
  }

  // Phase 6: New Generation Flow
  async getGenerationPreview(params: { hu_id: string; project_id: string; quantity: number; mode: string }): Promise<any> {
    return (await this.client.get('/generation/preview', { params })).data;
  }

  async startGenerationRun(payload: any): Promise<any> {
    return (await this.client.post('/generation/start', payload)).data;
  }

  async getGenerationRun(runId: string): Promise<any> {
    return (await this.client.get(`/generation/runs/${runId}`)).data;
  }

  async getLatestCases(entityId: string, projectId: string): Promise<any> {
    return (await this.client.get(`/generation/entities/${entityId}/cases/latest?project_id=${projectId}`)).data;
  }

  async updateCaseStatus(versionId: string, caseId: string, status: string, comment?: string): Promise<any> {
    return (await this.client.patch(`/generation/case-versions/${versionId}/cases/${caseId}/status`, { status, comment })).data;
  }

  // FlowSpecQA 2.1: Semantic Workflow
  async approveTestCase(caseId: string): Promise<any> {
    return (await this.client.post(`/generation/test-cases/${caseId}/approve`)).data;
  }

  async blockTestCase(caseId: string, reason: string): Promise<any> {
    return (await this.client.post(`/generation/test-cases/${caseId}/block`, { reason })).data;
  }

  async attachEvidence(stepId: string, evidence: { source_type: string, source_key: string, quote?: string, canonical_url?: string }): Promise<any> {
    return (await this.client.post(`/generation/test-steps/${stepId}/attach-evidence`, evidence)).data;
  }

  async updateTestCaseGranular(caseId: string, payload: { status?: string, priority?: string, review_meta?: any }): Promise<any> {
    return (await this.client.patch(`/generation/test-cases/${caseId}/status`, payload)).data;
  }

  async updateStepStatus(stepId: string, status: string, explanation?: string): Promise<any> {
    return (await this.client.patch(`/generation/test-steps/${stepId}/status`, { review_status: status, explanation })).data;
  }

  // ==========================================
  // History
  // ==========================================
  async getHistory(params?: { page?: number; pageSize?: number; huId?: string; status?: string; testType?: string; projectId?: string }) {
    const q: any = {};
    if (params?.page) q.page = params.page;
    if (params?.pageSize) q.page_size = params.pageSize;
    if (params?.huId) q.hu_id = params.huId;
    if (params?.status) q.status = params.status;
    if (params?.testType) q.test_type = params.testType;
    if (params?.projectId) q.project_id = params.projectId;

    return (await this.client.get('/history', { params: q })).data;
  }

  // ==========================================
  // Ingestion
  // ==========================================
  async getIngestionStatus(projectId?: string): Promise<any> {
    return (await this.client.get('/ingestion/status', { params: projectId ? { project_id: projectId } : undefined })).data;
  }

  async getRunStatus(runId: string): Promise<any> {
    return (await this.client.get(`/ingestion/run/${runId}`)).data;
  }

  async triggerIngestion(projectId: string): Promise<any> {
    return (await this.client.post('/ingestion/trigger', { project_id: projectId })).data;
  }

  // ==========================================
  // Evidence & Documents
  // ==========================================
  async getEvidenceDocuments(entityId: string, projectId: string): Promise<any[]> {
    return (await this.client.get(`/evidence/${entityId}/documents`, { params: { project_id: projectId } })).data;
  }

  async getEvidenceChunks(entityId: string, projectId: string): Promise<any[]> {
    return (await this.client.get(`/evidence/${entityId}/chunks`, { params: { project_id: projectId } })).data;
  }

  async uploadPdf(file: File, projectId: string, entityId?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    if (entityId) formData.append('entity_id', entityId);

    return (await this.client.post('/ingestion/upload', formData, {
      headers: { 'Content-Type': null },
      timeout: 600000 // 10 minutes for large uploads
    })).data;
  }

  // ==========================================
  // Users
  // ==========================================
  async getUsers(params?: { search?: string; role?: string; status?: string; page?: number; page_size?: number; }) {
    return (await this.client.get('/users', { params })).data;
  }

  async createUser(data: UserCreatePayload): Promise<User> {
    return (await this.client.post('/users', data)).data;
  }

  async updateUser(id: string, data: UserUpdatePayload): Promise<User> {
    return (await this.client.patch(`/users/${id}`, data)).data;
  }

  async deleteUser(id: string): Promise<any> {
    return (await this.client.delete(`/users/${id}`)).data;
  }

  // ==========================================
  // Prototypes
  // ==========================================
  async uploadPrototype(projectId: string, file: File, huId?: string, screenName?: string, context?: string, channel?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (huId) formData.append('hu_id', huId);
    if (screenName) formData.append('screen_name', screenName);
    if (context) formData.append('context', context);
    if (channel) formData.append('channel', channel);

    return (await this.client.post(`/projects/${projectId}/uploads/prototype`, formData, {
      headers: { 'Content-Type': null }
    })).data;
  }

  async processPrototype(projectId: string, artifactId: string): Promise<any> {
    return (await this.client.post(`/projects/${projectId}/uploads/${artifactId}/process`)).data;
  }

  async listUploads(projectId: string): Promise<any[]> {
    return (await this.client.get(`/projects/${projectId}/uploads`)).data;
  }

  // ==========================================
  // Billing
  // ==========================================
  async createCheckoutSession(planId: string = 'pro'): Promise<{ url: string }> {
    return (await this.client.post('/billing/checkout', { plan_id: planId })).data;
  }

  async createPortalSession(): Promise<{ url: string }> {
    const response = await this.client.post('/billing/portal');
    return response.data;
  }

  async simulatePaymentSuccess(orgId: string): Promise<any> {
    const response = await this.client.get(`/billing/simulate-success?org_id=${orgId}`);
    return response.data;
  }
  // ==========================================
  // Admin: AI Gateway
  // ==========================================
  async getAiGatewayConfigs(orgId?: string): Promise<any[]> {
    return (await this.client.get('/admin/ai-gateway', { params: { org_id: orgId } })).data;
  }

  async createAiGatewayConfig(payload: any): Promise<any> {
    return (await this.client.post('/admin/ai-gateway', payload)).data;
  }

  async deleteAiGatewayConfig(id: string): Promise<any> {
    return (await this.client.delete(`/admin/ai-gateway/${id}`)).data;
  }

  async verifyAiGatewayConfig(payload: any): Promise<{ success: boolean; message: string; details?: string }> {
    return (await this.client.post('/admin/ai-gateway/verify', payload)).data;
  }

  async bulkAssignLLMs(payload: any): Promise<any> {
    return (await this.client.post('/admin/projects/llm-assignment', payload)).data;
  }

  // ==========================================
  // Spec Review: Template Fields (Wizard)
  // ==========================================
  async getTemplateFields(category?: string): Promise<any[]> {
    const params = category ? { category } : {};
    return (await this.client.get('/spec-review/templates/fields', { params })).data.fields ?? [];
  }

  // ==========================================
  // Automation Memory
  // ==========================================
  async getMemoryStats(framework?: string): Promise<any> {
    const params = framework ? { framework } : {};
    return (await this.client.get('/automation/memory/stats', { params })).data;
  }

  async getMemoryRules(framework?: string, scope?: string, entityId?: string): Promise<any[]> {
    const params: any = {};
    if (framework) params.framework = framework;
    if (scope) params.scope = scope;
    if (entityId) params.entity_id = entityId;
    return (await this.client.get('/automation/memory', { params })).data;
  }

  async createMemoryRule(payload: any): Promise<any> {
    return (await this.client.post('/automation/memory', payload)).data;
  }

  async updateMemoryRule(ruleId: string, payload: any): Promise<any> {
    return (await this.client.patch(`/automation/memory/${ruleId}`, payload)).data;
  }

  // ==========================================
  // MCP Execution Results
  // ==========================================

  async getMcpResultsByEntity(entityId: string, limit = 20): Promise<any[]> {
    return (await this.client.get(`/automation/results/by-entity/${entityId}`, { params: { limit } })).data;
  }

  async getMcpResultsByRun(runId: string): Promise<any[]> {
    return (await this.client.get(`/automation/results/by-run/${runId}`)).data;
  }

  async getAutomationStatus(projectId: string, entityId: string): Promise<any> {
    return (await this.client.get(`/automation/entity-status`, { params: { project_id: projectId, entity_id: entityId } })).data;
  }

  // QA Triage (Fase 1 — ISTQB)
  async triageTestResult(
    execId: string,
    caseKey: string,
    payload: {
      qa_state: string;
      triage_type?: string;
      triage_note?: string;
      defect_link?: string;
      responsable?: string;
      accion_siguiente?: string;
      issue_id?: string | null;
      issue_status?: string | null;
    }
  ): Promise<any> {
    return (await this.client.patch(`/automation/execution-results/${execId}/triage/${encodeURIComponent(caseKey)}`, payload)).data;
  }

  async approveAllPassed(execId: string): Promise<any> {
    return (await this.client.post(`/automation/execution-results/${execId}/approve-all-passed`)).data;
  }

  async deleteExecutionResult(execId: string): Promise<any> {
    return (await this.client.delete(`/automation/execution-results/${execId}`)).data;
  }

  async addToPool(execId: string, caseKey: string): Promise<any> {
    return (await this.client.post(`/automation/execution-results/${execId}/add-to-pool/${encodeURIComponent(caseKey)}`)).data;
  }

  async quarantineTest(execId: string, caseKey: string, payload: { reason: string; until_date?: string }): Promise<any> {
    return (await this.client.post(`/automation/execution-results/${execId}/quarantine/${encodeURIComponent(caseKey)}`, payload)).data;
  }

  async getRegressionPool(entityId: string): Promise<any> {
    return (await this.client.get(`/automation/regression-pool`, { params: { entity_id: entityId } })).data;
  }

  async getDiscoveryPlan(projectId: string, entityId: string): Promise<any> {
    return (await this.client.get(`/automation/projects/${projectId}/entities/${entityId}/discovery-plan`)).data;
  }

  // ==========================================
  // Phase 9: Relational Discovery Evidence
  // ==========================================
  async getEntityEvidence(entityId: string): Promise<any> {
    return (await this.client.get(`/discovery/evidence/entity/${entityId}`)).data;
  }

  // Dejamos que verifyEvidence acepte payload (de main) o string de notes (de elegant-ramanujan)
  async verifyEvidence(recordId: string, payload: any = {}): Promise<any> {
    if (payload && (payload.dom_elements !== undefined || payload.notes !== undefined)) {
      return (await this.client.post(`/discovery/evidence/${recordId}/verify`, payload)).data;
    }
    return (await this.client.post(`/automation/discovery/evidence/${recordId}/verify`, payload)).data;
  }

  async rejectEvidence(recordId: string, reason: string): Promise<any> {
    try {
      return (await this.client.post(`/automation/discovery/evidence/${recordId}/reject`, { reason })).data;
    } catch {
      return (await this.client.post(`/discovery/evidence/${recordId}/reject`, { reason })).data;
    }
  }

  // ── Phase 9 / 10 — Evidence Corpus relational + UI Validation ──────────────
  // DISCOVERY_SPEC.md § 9.3 + § 10. Backend endpoints created in Sprint 3.

  /** Latest Discovery session for an entity (relational with JSONB fallback). */
  async getDiscoverySession(entityId: string): Promise<any> {
    return (await this.client.get(`/automation/discovery/sessions/${entityId}/latest`)).data;
  }

  /** Lightweight summary of the legacy JSONB corpus. */
  async getDiscoveryCorpusSummary(entityId: string): Promise<any> {
    return (await this.client.get(`/automation/discovery/corpus/${entityId}/summary`)).data;
  }

  /** List EvidenceRecords for an entity, with optional filters. */
  async listEvidenceRecords(
    entityId: string,
    opts?: { sessionId?: string; status?: string; limit?: number },
  ): Promise<{ entity_id: string; count: number; records: any[] }> {
    const params: Record<string, any> = {};
    if (opts?.sessionId) params.session_id = opts.sessionId;
    if (opts?.status)    params.status     = opts.status;
    if (opts?.limit)     params.limit      = opts.limit;
    return (
      await this.client.get(`/automation/discovery/evidence/${entityId}`, { params })
    ).data;
  }

  /** Screen baselines for an entity (used by Phase 11 incremental Discovery). */
  async getDiscoveryBaseline(entityId: string): Promise<{
    entity_id: string;
    count: number;
    baselines: any[];
  }> {
    return (await this.client.get(`/automation/discovery/baseline/${entityId}`)).data;
  }

  // ── Skope Scans ──────────────────────────────────────────────────────────────
  async getScan(scanId: string): Promise<any> {
    return (await this.client.get(`/skope/scans/${scanId}`)).data;
  }

  async listScans(params?: { project_id?: string; entity_id?: string; page?: number; page_size?: number }): Promise<any> {
    return (await this.client.get(`/skope/scans`, { params })).data;
  }

  async getComplianceTrend(params: { entity_id?: string; project_id?: string; limit?: number }): Promise<any> {
    return (await this.client.get(`/skope/scans/compliance-trend`, { params })).data;
  }

  // ── Performance / K6 ─────────────────────────────────────────────────────────
  async listK6Executions(params?: {
    project_id?: string;
    capture_id?: string;
    skope_scan_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<any> {
    return (await this.client.get(`/performance/executions`, { params })).data;
  }

  async getK6Execution(executionId: string): Promise<any> {
    return (await this.client.get(`/performance/executions/${executionId}`)).data;
  }

  async listCaptures(params?: {
    project_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<any> {
    return (await this.client.get(`/performance/captures`, { params })).data;
  }

  async getCapture(captureId: string): Promise<any> {
    return (await this.client.get(`/performance/captures/${captureId}`)).data;
  }
}

export const api = new ApiClientService();
export default api;
